import React from "react";
import { X, FileText, Package, User, Clock, Printer } from "lucide-react";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

export default function DebitMemoModal({ returnRecord, onClose }) {
  if (!returnRecord) return null;

  const totalReplace = (returnRecord.items || []).reduce(
    (s, i) => s + (i.replaceQty || 0),
    0,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
      <div className="relative flex flex-col w-full max-w-3xl max-h-[90vh] rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="shrink-0 border-b bg-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500 rounded-xl text-white">
              <FileText size={18} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900">Debit Memo</h2>
              <p className="text-xs text-slate-500 font-mono">
                {returnRecord.salesOrderReturnNumber}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-200 transition"
            >
              <Printer size={13} /> Print
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Document header info */}
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[10px] font-black uppercase text-slate-400 mb-1">
                  Debit Memo Number
                </p>
                <p className="font-black text-slate-800 font-mono text-lg">
                  {returnRecord.salesOrderReturnNumber}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase text-slate-400 mb-1">
                  Original DO-R- Reference
                </p>
                <p className="font-bold text-indigo-700 font-mono">
                  {returnRecord.returnNumber}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase text-slate-400 mb-1">
                  Delivery Order
                </p>
                <p className="font-bold text-slate-800">
                  {returnRecord.deliveryOrderNumber}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase text-slate-400 mb-1">
                  Customer
                </p>
                <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                  <User size={12} className="text-slate-400" />{" "}
                  {returnRecord.customerName}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase text-slate-400 mb-1">
                  Date Processed
                </p>
                <div className="flex items-center gap-1 text-slate-700 text-xs">
                  <Clock size={11} className="text-slate-400" />{" "}
                  {formatDate(returnRecord.createdDate)}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase text-slate-400 mb-1">
                  Processed By
                </p>
                <p className="text-slate-700 text-xs font-semibold">
                  {returnRecord.createdByFullName || "—"}
                </p>
              </div>
            </div>

            {returnRecord.reason && (
              <div className="mt-4 pt-4 border-t border-orange-200 text-xs">
                <span className="font-black uppercase text-slate-400">
                  Reason for Return:{" "}
                </span>
                <span className="text-slate-600 italic">
                  {returnRecord.reason}
                </span>
              </div>
            )}
            {returnRecord.note && (
              <div className="mt-2 text-xs">
                <span className="font-black uppercase text-slate-400">
                  Note:{" "}
                </span>
                <span className="text-slate-600 italic">
                  {returnRecord.note}
                </span>
              </div>
            )}
          </div>

          {/* Items table */}
          <div>
            <h3 className="text-xs font-black uppercase text-slate-500 mb-2 flex items-center gap-2">
              <Package size={13} /> Returned Items
            </h3>
            <div className="overflow-x-auto border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b text-[10px] font-black uppercase text-slate-500">
                  <tr>
                    <th className="p-3 pl-5">Product</th>
                    <th className="p-3 text-center">UOM</th>
                    <th className="p-3 text-center">Qty Returned</th>
                    <th className="p-3 text-center text-red-600">
                      Cash Return
                    </th>
                    <th className="p-3 text-center text-blue-600">
                      Replace Item
                    </th>
                    <th className="p-3 text-right pr-5">Refund Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(returnRecord.items || []).map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="p-3 pl-5">
                        <div className="font-bold text-slate-800">
                          {item.productName}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {formatCurrency(item.unitPrice)} / unit
                        </div>
                      </td>
                      <td className="p-3 text-center text-xs text-slate-500">
                        {item.salesUomName || "Unit"}
                      </td>
                      <td className="p-3 text-center font-black text-slate-700">
                        {item.quantityReturned}
                      </td>
                      <td className="p-3 text-center font-black text-red-600">
                        {item.cashQty}
                      </td>
                      <td className="p-3 text-center font-black text-blue-600">
                        {item.replaceQty}
                      </td>
                      <td className="p-3 text-right pr-5 font-black text-red-600">
                        {formatCurrency(item.refundAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td
                      colSpan={3}
                      className="p-3 pl-5 text-xs font-black uppercase text-slate-400"
                    >
                      Summary
                    </td>
                    <td className="p-3 text-center font-black text-red-600">
                      {(returnRecord.items || []).reduce(
                        (s, i) => s + (i.cashQty || 0),
                        0,
                      )}
                    </td>
                    <td className="p-3 text-center font-black text-blue-600">
                      {totalReplace}
                    </td>
                    <td className="p-3 text-right pr-5 font-black text-red-600 text-lg">
                      {formatCurrency(returnRecord.totalRefundAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Footer totals */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
              <p className="text-[10px] font-black uppercase text-red-400 mb-1">
                Total Cash Refund
              </p>
              <p className="text-2xl font-black text-red-600">
                {formatCurrency(returnRecord.totalRefundAmount)}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
              <p className="text-[10px] font-black uppercase text-blue-400 mb-1">
                Total Replace Items
              </p>
              <p className="text-2xl font-black text-blue-600">
                {totalReplace} unit{totalReplace !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
